import { supabase } from '@/integrations/supabase/client';

export interface StorePointConfig {
  isDevelopment: boolean;
  popupUrl: string;
  apiUrl: string;
  configComplete: boolean;
  environment: string;
}

export interface ShopInfo {
  environment: string;
  isDevelopment: boolean;
  configComplete: boolean;
  apiUrls: {
    popup: string;
    api: string;
  };
  shopDetails: {
    name: string;
    id: string;
    version: string;
    adminEmail: string | null;
  };
  features: {
    pointsEnabled: boolean;
    receiptGeneration: boolean;
    balanceSync: boolean;
    transactionLogging: boolean;
  };
  limits: {
    maxPointsPerTransaction: number;
    minPointsPerTransaction: number;
    syncIntervalMinutes: number;
  };
}

export interface PointsBalance {
  points: number;
  lastSynced: Date;
  isValid: boolean;
  errorCode?: string;
  linkExpired?: boolean;
}

export interface SyncResult {
  success: boolean;
  errorCode?: string;
  linkExpired?: boolean;
}

export const normalizeDartsbeatRespCode = (code?: string | null): string => {
  if (!code) return 'UNKNOWN_ERROR';

  let normalized = code.trim();

  if (normalized.startsWith('e_DARTSBET')) {
    normalized = normalized.replace('e_DARTSBET', 'e_DARTSBEAT');
  }

  const map: Record<string, string> = {
    'e_DARTSBEAT_ERR_AD_SP_NO': 'e_DARTSBEAT_ERR_AD_SP_NO_KEY',
    'e_DARTSBEAT_SHOP_ERR_SHOPKEY_DIFFERNT': 'e_DARTSBEAT_SHOP_ERR_SHOPKEY_DIFFERENT',
    'e_DARTSBEAT_SHOP_ERR_USERKEY_DIFFEENT': 'e_DARTSBEAT_SHOP_ERR_USERKEY_DIFFERENT',
    'e_DARTSBEAT_SHOP_ERR_SPPKEY_DIFFERENT': 'e_DARTSBEAT_SHOP_ERR_SPKEY_DIFFERENT',
    'e_DARTSBEAT_SHOP_ERR_SPPKEY_DIFFERNT': 'e_DARTSBEAT_SHOP_ERR_SPKEY_DIFFERENT',
    'e_DARTSBEAT_SHOP_INFO_REQ': 'e_DARTSBEAT_SHOP_INFO',
    'e_DARTSBET_SHOP_INFO_REQ': 'e_DARTSBEAT_SHOP_INFO',
    'e_DARTSBET_SHOP_INFO': 'e_DARTSBEAT_SHOP_INFO',
    'e_DARTSBET_SHOP_ERR_PASS': 'e_DARTSBEAT_SHOP_ERR_PASS',
    // Normalize typos in "already registered" errors (matches backend normalization)
    'e_DARTSBEAT_SHOP_ERR_ALEADY_REGIST': 'e_DARTSBEAT_SHOP_ERR_ALEADY_SIGN',
    'e_DARTSBEAT_SHOP_ERR_ALREADY_REGIST': 'e_DARTSBEAT_SHOP_ERR_ALEADY_SIGN',
    'e_DARTSBEAT_SHOP_ERR_ALREADY_SIGN': 'e_DARTSBEAT_SHOP_ERR_ALEADY_SIGN'
  };

  if (map[normalized]) {
    normalized = map[normalized];
  }

  return normalized;
};

/**
 * Get shop info from secure server-side endpoint
 */
export const getShopInfo = async (): Promise<ShopInfo> => {
  try {
    const { data, error } = await supabase.functions.invoke('dartsbeat-shop-info');
    
    if (error) throw error;
    
    return data;
  } catch (error) {
    console.error('Error fetching shop info:', error);
    
    // Fallback to safe defaults when Edge Function is unavailable
    const isDevelopment = import.meta.env.VITE_DARTSBEAT_ENVIRONMENT === 'development';

    return {
      environment: import.meta.env.VITE_DARTSBEAT_ENVIRONMENT || 'development',
      isDevelopment,
      configComplete: false,
      apiUrls: {
        // These URLs are no longer used since we removed popup-based connection
        popup: isDevelopment
          ? 'https://dartsbeat-tournament.com/public/storepoint/dartsbeat_login.html'
          : 'https://dartsbeat.com/public/storepoint/dartsbeat_login.html',
        api: isDevelopment
          ? 'https://dartsbeat-tournament.com/process/'
          : 'https://dartsbeat.com/process/'
      },
      shopDetails: {
        name: 'Firedarts Store',
        id: 'firedarts',
        version: '1.0.0',
        adminEmail: null
      },
      features: {
        pointsEnabled: false,
        receiptGeneration: false,
        balanceSync: false,
        transactionLogging: false
      },
      limits: {
        maxPointsPerTransaction: 1000,
        minPointsPerTransaction: 1,
        syncIntervalMinutes: 5
      }
    };
  }
};

/**
 * Get StorePoint configuration (legacy - use getShopInfo instead)
 * @deprecated Use getShopInfo() for secure server-side configuration
 */
export const getStorePointConfig = async (): Promise<StorePointConfig> => {
  const shopInfo = await getShopInfo();
  
  return {
    isDevelopment: shopInfo.isDevelopment,
    popupUrl: shopInfo.apiUrls.popup,
    apiUrl: shopInfo.apiUrls.api,
    configComplete: shopInfo.configComplete,
    environment: shopInfo.environment,
  };
};

/**
 * Validate StorePoint configuration
 */
export const validateStorePointConfig = (config: StorePointConfig): boolean => {
  return config.configComplete && !!config.popupUrl && !!config.apiUrl;
};

/**
 * Generate shop user key for a user
 */
export const generateShopUserKey = (userId: string): string => {
  return `dartsbeat_pt_${userId.substring(0, 8)}`;
};

/**
 * Fetch current points balance from StorePoint API using secure server-side credentials
 */
export const fetchPointsBalance = async (userId: string): Promise<PointsBalance> => {
  const startTime = Date.now();
  console.log(`⏱️ [UserInfo] Starting balance fetch for user ${userId}`);

  try {
    const { data, error } = await supabase.functions.invoke('dartsbeat-api', {
      body: {
        action: 'login'
        // Email and dbkey are retrieved securely server-side by user ID
      }
    });

    const duration = Date.now() - startTime;
    console.log(`⏱️ [UserInfo] Balance fetch completed in ${duration}ms`);

    if (error) throw error;

    // Handle new response format from dartsbeat-api
    if (!data?.success) {
      const errorCode = normalizeDartsbeatRespCode(data?.error || data?.data?.RESP);
      const linkExpired = Boolean(data?.linkExpired);
      console.warn(`❌ [UserInfo] DartsBeat login failed: ${errorCode}`);
      return {
        points: 0,
        lastSynced: new Date(),
        isValid: false,
        errorCode,
        linkExpired
      };
    }

    const dartsbeatResponse = data.data;
    const respCode = normalizeDartsbeatRespCode(dartsbeatResponse.RESP);

    if (respCode === 'e_DARTSBEAT_SUC') {
      console.log(`✅ [UserInfo] Points fetched: ${dartsbeatResponse.DBPOINT}`);
      return {
        points: parseInt(dartsbeatResponse.DBPOINT),
        lastSynced: new Date(),
        isValid: true
      };
    }

    const failureCode = respCode || 'UNKNOWN_ERROR';
    console.warn(`❌ [UserInfo] Unexpected response: ${failureCode}`);
    return {
      points: 0,
      lastSynced: new Date(),
      isValid: false,
      errorCode: failureCode,
      linkExpired: false
    };

  } catch (error) {
    console.error('Error fetching points balance:', error);
    return {
      points: 0,
      lastSynced: new Date(),
      isValid: false,
      errorCode: error instanceof Error ? error.message : 'UNKNOWN_ERROR'
    };
  }
};

/**
 * Sync user points balance with StorePoint
 */
export const syncUserPoints = async (userId: string): Promise<SyncResult> => {
  const syncStartTime = Date.now();
  console.log(`🔄 [Sync] Starting point sync for user ${userId}`);

  try {
    // Check if user has a connection (using safe profile view)
    const { data: profile, error: profileError } = await supabase
      .from('profile_safe')
      .select('connected_account_id, is_connected, has_credentials')
      .eq('id', userId)
      .single();

    if (profileError || !profile?.connected_account_id || !profile?.is_connected || !profile?.has_credentials) {
      console.log(`❌ [Sync] User not connected or missing credentials`);
      return { success: false, errorCode: 'not_connected' };
    }

    console.log(`📡 [Sync] Calling UserInfo API to fetch latest balance...`);

    // Fetch current balance using secure server-side credentials
    const balance = await fetchPointsBalance(userId);

    if (!balance.isValid) {
      console.log(`❌ [Sync] Balance fetch returned invalid result (${balance.errorCode || 'unknown'})`);

      const errorCode = balance.errorCode || 'UNKNOWN_ERROR';

      await supabase
        .from('dartsbeat_connections')
        .insert({
          user_id: userId,
          action: 'sync',
          db_email: profile.connected_account_id,
          success: false,
          error_message: errorCode
        });

      return {
        success: false,
        errorCode,
        linkExpired: balance.linkExpired
      };
    }

    const syncDuration = Date.now() - syncStartTime;
    console.log(`✅ [Sync] Complete! Total sync time: ${syncDuration}ms, New balance: ${balance.points}`);

    // Balance updates are now handled automatically by the Edge Function
    // No client-side credential updates needed - everything is server-side

    // Log sync operation
    await supabase
      .from('dartsbeat_connections')
      .insert({
        user_id: userId,
        action: 'sync',
        db_email: profile.connected_account_id,
        success: true
      });

    return { success: true };

  } catch (error) {
    console.error('Error syncing user points:', error);
    
    // Log failed sync
    await supabase
      .from('dartsbeat_connections')
      .insert({
        user_id: userId,
        action: 'sync',
        success: false,
        error_message: error instanceof Error ? error.message : 'Unknown error'
      });

    return {
      success: false,
      errorCode: error instanceof Error ? error.message : 'unknown_error'
    };
  }
};

/**
 * Check if points balance needs refreshing
 */
export const shouldRefreshBalance = (lastSynced: string | null): boolean => {
  if (!lastSynced) return true;
  
  const lastSyncDate = new Date(lastSynced);
  const now = new Date();
  const diffMinutes = (now.getTime() - lastSyncDate.getTime()) / (1000 * 60);
  
  // Refresh if last sync was more than 5 minutes ago
  return diffMinutes > 5;
};

/**
 * Validate if user has sufficient points for a purchase
 */
export const validateSufficientPoints = async (
  userId: string,
  requiredPoints: number,
  forceSync: boolean = true // SECURITY: Always sync for purchase validation
): Promise<{ valid: boolean; currentBalance: number; message?: string }> => {
  try {
    // Get current profile using safe view
    const { data: profile, error } = await supabase
      .from('profile_safe')
      .select('points, connected_account_id, points_last_synced, is_connected, has_credentials')
      .eq('id', userId)
      .single();

    if (error || !profile) {
      return { valid: false, currentBalance: 0, message: 'User profile not found' };
    }

    if (!profile.connected_account_id || !profile.is_connected || !profile.has_credentials) {
      return { valid: false, currentBalance: 0, message: 'DartsBeat account not connected' };
    }

    // SECURITY: For purchase validation, ALWAYS sync with DartsBeat to get real-time balance
    // This prevents race conditions across multiple sites using the same StorePoint account
    // Even if forceSync=false is passed, we ignore it for security
    const syncResult = await syncUserPoints(userId);
    if (!syncResult.success) {
      const fallbackBalance = profile.points || 0;
      let message = 'Failed to sync balance';

      if (syncResult.linkExpired) {
        message = 'A ligação à conta DartsBeat expirou. Por favor, reconecte.';
      } else if (syncResult.errorCode) {
        message = getStorePointErrorMessage(syncResult.errorCode);
      }

      return { valid: false, currentBalance: fallbackBalance, message };
    }

    // Refetch updated profile using safe view
    const { data: updatedProfile } = await supabase
      .from('profile_safe')
      .select('points')
      .eq('id', userId)
      .single();

    const currentBalance = updatedProfile?.points || 0;
    const valid = currentBalance >= requiredPoints;

    return {
      valid,
      currentBalance,
      message: valid ? undefined : `Insufficient points. Required: ${requiredPoints}, Available: ${currentBalance}`
    };

  } catch (error) {
    console.error('Error validating points:', error);
    return {
      valid: false,
      currentBalance: 0,
      message: 'Error validating points balance'
    };
  }
};

/**
 * Process a points purchase
 */
export const processPointsPurchase = async (
  userId: string,
  itemKey: string,
  pointsCost: number,
  receiptId?: string,
  storeId?: string
): Promise<{ success: boolean; newBalance?: number; message?: string }> => {
  try {
    // OPTIMIZATION: Don't pre-validate balance - let DartsBeat API handle it
    // This reduces timing from ~1200ms to ~400-600ms
    // DartsBeat Shop_Payment API already validates balance atomically
    // Pre-validation creates a race condition window anyway

    // Generate receipt ID if not provided
    const receipt = receiptId || generateReceiptId();

    // Get current balance for logging (non-blocking, best effort)
    let balanceBefore = 0;
    try {
      const { data: profile } = await supabase
        .from('profile_safe')
        .select('points')
        .eq('id', userId)
        .single();
      balanceBefore = profile?.points || 0;
    } catch (err) {
      console.warn('Could not fetch balance before purchase:', err);
    }

    // Call purchase API (credentials handled securely server-side)
    // DartsBeat validates balance atomically and deducts points
    const { data, error } = await supabase.functions.invoke('dartsbeat-api', {
      body: {
        action: 'purchase',
        itemKey,
        price: pointsCost,
        receipt
      }
    });

    if (error) throw error;

    // Handle new response format from dartsbeat-api
    if (!data?.success) {
      throw new Error('Erro ao processar a compra');
    }

    const dartsbeatResponse = data.data;
    const respCode = normalizeDartsbeatRespCode(dartsbeatResponse.RESP);

    if (respCode === 'e_DARTSBEAT_SUC') {
      const newBalance = parseInt(dartsbeatResponse.DBPOINT);
      const confirmedReceipt = dartsbeatResponse.RECEIPT || receipt;

      // Attempt to fetch product details once more for order logging
      let productDetails: { store_id?: string; price?: number } | null = null;
      try {
        const { data: productInfo } = await supabase
          .from('products')
          .select('store_id, price')
          .eq('id', itemKey)
          .single();
        if (productInfo) {
          productDetails = productInfo;
        }
      } catch (productFetchError) {
        console.warn('Could not fetch product details for order record:', productFetchError);
      }

      // Create order history entry tied to receipt
      try {
        const { data: orderRecord, error: orderError } = await supabase
          .from('orders')
          .insert({
            user_id: userId,
            points_used: pointsCost,
            status: 'pending',
            store_id: storeId ?? productDetails?.store_id ?? null,
            receipt_id: confirmedReceipt
          })
          .select('id')
          .single();

        if (orderError) {
          console.error('Failed to create order record:', orderError);
        } else if (orderRecord?.id) {
          const { error: orderItemError } = await supabase
            .from('order_items')
            .insert({
              order_id: orderRecord.id,
              product_id: itemKey,
              quantity: 1,
              price: productDetails?.price ?? 0,
              points_price: pointsCost
            });

          if (orderItemError) {
            console.error('Failed to create order item:', orderItemError);
          }
        }
      } catch (orderException) {
        console.error('Error while recording order history:', orderException);
      }

      // Balance updates are now handled automatically by the Edge Function
      // No client-side credential updates needed - everything is server-side

      // Log transaction
      await supabase
        .from('points_ledger')
        .insert({
          user_id: userId,
          transaction_type: 'purchase',
          amount: -pointsCost,
          balance_before: balanceBefore,
          balance_after: newBalance,
          external_ref: confirmedReceipt,
          metadata: { item_key: itemKey }
        });

      return { success: true, newBalance };
    }

    // Handle purchase errors with user-friendly Portuguese messages
    const errorMessages: Record<string, string> = {
      'e_DARTSBEAT_SP_NOT_ENOUGH': 'Storepoints insuficientes para esta troca',
      'e_DARTSBEAT_ERR_DUPLE_PAYMENT': 'Esta transação já foi processada anteriormente',
      'e_DARTSBEAT_SHOP_ERR_PASS': 'A ligação à conta DartsBeat expirou. Por favor, reconecte.'
    };

    const message = errorMessages[respCode] || 'Falha na compra. Tente novamente.';

    // Special handling for specific error types
    if (respCode === 'e_DARTSBEAT_ERR_DUPLE_PAYMENT') {
    } else if (respCode === 'e_DARTSBEAT_SP_NOT_ENOUGH') {
    }

    return { success: false, message };

  } catch (error) {
    console.error('Error processing purchase:', error);
    return {
      success: false,
      message: 'Erro ao processar a compra. Tente novamente.'
    };
  }
};

/**
 * Generate a unique receipt ID for purchases
 */
export const generateReceiptId = (): string => {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return `FR_${timestamp}_${random}`.toUpperCase();
};

/**
 * Get comprehensive DartsBeat error message with Portuguese localization
 */
export const getStorePointErrorMessage = (responseCode: string): string => {
  const normalized = normalizeDartsbeatRespCode(responseCode);

  const errorMessages: Record<string, string> = {
    // Connection/Authentication Errors
    'e_DARTSBEAT_SHOP_ERR_KEY': 'Chave StorePoint incorreta.',
    'e_DARTSBEAT_SHOP_ERR_PASS': 'A palavra-passe foi alterada. Por favor, reconecte a sua conta.',
    'e_DARTSBEAT_SHOP_ERR_USERKEY_DIFFERENT': 'Chave de utilizador não corresponde.',
    'e_DARTSBEAT_SHOP_ERR_SHOPKEY_DIFFERENT': 'Chave da loja não corresponde.',
    'e_DARTSBEAT_SHOP_ERR_SPKEY_DIFFERENT': 'Chave StorePoint não foi encontrada.',
    'e_DARTSBEAT_SERVER_ERR': 'Não foi possível ligar ao servidor DartsBeat. Tente novamente mais tarde.',

    // Account Linking Errors
    'e_DARTSBEAT_SHOP_ERR_ALEADY_SIGN': 'Esta conta já está ligada a outro utilizador. Desvincule primeiro.',
    'e_DARTSBEAT_SHOP_ERR_ALEADY_SIGN_EQUAL': 'Este utilizador já está ligado a esta conta.',
    'ERR_DARTSBEAT_EMAIL_ALREADY_ASSIGNED': 'Esta conta DartsBeat já está ligada a outra conta FireDarts. Desvincule primeiro ou use outra conta.',
    'e_DARTSBEAT_USER_DEL': 'Utilizador foi eliminado ou não existe.',
    'e_DARTSBEAT_SHOP_ERR_NO_SPDATA': 'Conta DartsBeat não encontrada. Registe novamente.',

    // Account Status Errors
    'e_DARTSBEAT_SHOP_ERR_SP_STOP': 'A sua conta Store Points foi suspensa.',

    // Transaction Errors
    'e_DARTSBEAT_SP_NOT_ENOUGH': 'Storepoints insuficientes para esta troca.',
    'e_DARTSBEAT_ERR_DUPLE_PAYMENT': 'Esta transação já foi processada anteriormente.',

    // Data/Validation Errors
    'e_DARTSBEAT_ERR_DATA_PARSER': 'Dados inválidos fornecidos.',

    // Generic Success (should not be treated as error)
    'e_DARTSBEAT_SUC': 'Operação realizada com sucesso.',
  };

  return errorMessages[normalized] || `Erro desconhecido: ${normalized}`;
};

/**
 * Check if response code indicates connection expiry requiring reconnect
 */
export const isConnectionExpired = (responseCode: string): boolean => {
  const normalized = normalizeDartsbeatRespCode(responseCode);
  return normalized === 'e_DARTSBEAT_SHOP_ERR_PASS' || normalized === 'e_DARTSBEAT_SHOP_ERR_NO_SPDATA';
};

/**
 * Check if response code indicates duplicate payment
 */
export const isDuplicatePayment = (responseCode: string): boolean => {
  return normalizeDartsbeatRespCode(responseCode) === 'e_DARTSBEAT_ERR_DUPLE_PAYMENT';
};

/**
 * Check if response code indicates insufficient points
 */
export const isInsufficientPoints = (responseCode: string): boolean => {
  return normalizeDartsbeatRespCode(responseCode) === 'e_DARTSBEAT_SP_NOT_ENOUGH';
};
